// Reddit Pixel for Reddit Ads. Same policy as google-ads-tag.js and
// linkedin-insight.js: production ad hosts only, never internal devices, and
// only after the visitor accepts the marketing category in the cookie banner
// (GPC wins, see scripts/consent.js). Reddit's snippet is an inline <script>;
// the site CSP forbids inline scripts, so the same setup runs here instead.
//
// The pixel id comes from VITE_REDDIT_PIXEL_ID at build time (the production
// build reads the repository variable of the same name). Unset, nothing loads.
//
// A Reddit ad click lands with ?rdt_cid=<click id>. With consent, that id is
// kept in a first-party `rdt_cid` cookie (28 days) so ScopeWorth.App can send
// it with SignUp and Purchase to the Reddit Conversions API, alongside the
// pixel's own `_rdt_uuid` cookie.

import { hasConsent, onConsentChange } from '../scripts/consent.js';
import { isInternalDevice } from './posthog.js';

const PIXEL_ID = String(import.meta.env?.VITE_REDDIT_PIXEL_ID || '').trim();
const AD_HOSTS = ['scopeworth.app', 'www.scopeworth.app', 'getscopeworth.com', 'www.getscopeworth.com'];
export const CLICK_ID_COOKIE = 'rdt_cid';
const CLICK_ID_MAX_AGE = 60 * 60 * 24 * 28; // 28 days, Reddit's click attribution window

let loaded = false;

function allowed() {
  return !!PIXEL_ID && !isInternalDevice() && hasConsent('marketing')
    && AD_HOSTS.includes(window.location.hostname);
}

/** Store ?rdt_cid in a first-party cookie the App can read on the same apex. */
export function rememberClickId() {
  let clickId = null;
  try { clickId = new URL(window.location.href).searchParams.get('rdt_cid'); } catch (_) { return; }
  if (!clickId) return;
  clickId = clickId.trim();
  // Reddit click ids are opaque tokens; refuse anything that is not one.
  if (!/^[A-Za-z0-9._~-]{1,200}$/.test(clickId)) return;
  const host = window.location.hostname;
  // The App lives on scopeworth.app: scope the cookie to the apex there so
  // www. visits reach it too. The getscopeworth.com alias keeps it host-only.
  const domain = host === 'scopeworth.app' || host.endsWith('.scopeworth.app') ? '; domain=scopeworth.app' : '';
  const secure = window.location.protocol === 'https:' ? '; secure' : '';
  document.cookie = `${CLICK_ID_COOKIE}=${encodeURIComponent(clickId)}${domain}; path=/; max-age=${CLICK_ID_MAX_AGE}; samesite=Lax${secure}`;
}

function loadRedditPixel() {
  if (loaded || !allowed()) return;
  loaded = true;

  rememberClickId();

  if (!window.rdt) {
    const rdt = function rdt() {
      if (rdt.sendEvent) rdt.sendEvent.apply(rdt, arguments);
      else rdt.callQueue.push(arguments);
    };
    rdt.callQueue = [];
    window.rdt = rdt;
  }
  window.rdt('init', PIXEL_ID);
  window.rdt('track', 'PageVisit');

  const s = document.createElement('script');
  s.async = true;
  s.src = 'https://www.redditstatic.com/ads/pixel.js';
  document.head.appendChild(s);
}

loadRedditPixel();

// Accepting later loads the pixel without a reload; the banner reloads on withdrawal.
onConsentChange((consent) => {
  if (consent.marketing) loadRedditPixel();
});
